import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Sparkles,
  ArrowRight,
  CheckCircle2,
  Loader2,
  Send,
  Briefcase,
  GraduationCap,
  Handshake,
  Award,
  Users,
  Heart,
} from "lucide-react";
import { Container, FadeIn, SectionHeading, SectionLabel } from "@/components/Section";
import {
  LEVEL_UP_IMG,
  LEVEL_UP_FOR,
  LEVEL_UP_TRACKS,
  LEVEL_UP_OUTCOMES,
  LEVEL_UP_PROGRESSION,
  LEVEL_UP_INSTITUTIONS,
  LEVEL_UP_PARTNERS,
  PARTNER_ENGAGEMENT,
  LEVEL_UP_INTERESTS,
  slug,
} from "@/lib/levelUp";
import SEOHead from "@/components/SEOHead";
import NextStepsBlock from "@/components/NextStepsBlock";
import PartnersStrip from "@/components/PartnersStrip";
import AcuityScheduleBlock from "@/components/AcuityScheduleBlock";
import StayConnected from "@/components/StayConnected";
import { IMG } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialApp = {
  programme: "Level Up XR",
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  age: "",
  city: "",
  interests: [],
  portfolio_url: "",
  why_join: "",
  referral_source: "",
};

const initialPartner = {
  organisation: "",
  organisation_type: "Youth organisation",
  contact_name: "",
  role: "",
  email: "",
  phone: "",
  location: "",
  participant_count: "",
  engagement_modes: [],
  message: "",
};

export default function LevelUpXR() {
  const [tab, setTab] = useState("apply"); // apply | partner
  const [appForm, setAppForm] = useState(initialApp);
  const [partnerForm, setPartnerForm] = useState(initialPartner);
  const [submitting, setSubmitting] = useState(false);
  const [appDone, setAppDone] = useState(false);
  const [partnerDone, setPartnerDone] = useState(false);
  const formRef = useRef(null);

  const onAppChange = (e) =>
    setAppForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const onPartnerChange = (e) =>
    setPartnerForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const toggleAppInterest = (i) =>
    setAppForm((f) => ({
      ...f,
      interests: f.interests.includes(i)
        ? f.interests.filter((x) => x !== i)
        : [...f.interests, i],
    }));

  const togglePartnerMode = (m) =>
    setPartnerForm((f) => ({
      ...f,
      engagement_modes: f.engagement_modes.includes(m)
        ? f.engagement_modes.filter((x) => x !== m)
        : [...f.engagement_modes, m],
    }));

  const submitApp = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/applications`, appForm);
      toast.success("Application received. We'll be in touch within 5 working days.");
      setAppDone(true);
      setAppForm(initialApp);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : detail;
      toast.error(msg || "Could not send application. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const submitPartner = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/partnerships`, partnerForm);
      toast.success("Partnership enquiry received. We'll be in touch shortly.");
      setPartnerDone(true);
      setPartnerForm(initialPartner);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : detail;
      toast.error(msg || "Could not send enquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToForm = (which = "apply") => {
    setTab(which);
    setTimeout(
      () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
      40,
    );
  };

  return (
    <div data-testid="level-up-xr-page">
      <SEOHead
        cmsPage="level-up"
        title="LEVEL UP · XR & Immersive Arts Programme for 16–25 · TOK"
        description="LEVEL UP is the Temple of Kulture's flagship youth development pathway for ages 16–25 — XR, immersive arts, AI storytelling, filmmaking, performance, music and digital world-building. Progression routes into University of Kent, Rose Bruford and the creative industries."
        image={IMG.levelUp}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "EducationalOccupationalProgram",
          name: "LEVEL UP — TOK Youth Programme",
          description: "Annual XR, immersive arts, AI storytelling, filmmaking, performance and digital world-building pathway for 16–25 year olds, with progression into University of Kent and Rose Bruford.",
          provider: { "@type": "Organization", name: "Temple of Kulture" },
          educationalProgramMode: "FullTime",
          occupationalCategory: "Creative industries",
        }}
      />
      {/* HERO */}
      <section className="relative min-h-[85vh] flex items-end overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center scale-105"
          style={{ backgroundImage: `url(${LEVEL_UP_IMG.hero})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/80 to-[#050505]/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/30 to-transparent" />

        <Container className="relative pt-44 pb-20 w-full">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="max-w-5xl"
          >
            <p className="text-[11px] uppercase tracking-[0.4em] text-amber-400 font-bold mb-8 flex items-center gap-3">
              <span className="inline-block h-2 w-2 bg-amber-400 tok-pulse-gold" />
              The creative future lab · Ages 16–25
            </p>
            <h1 className="font-display font-black uppercase leading-[0.9] tracking-tighter text-6xl sm:text-7xl md:text-8xl lg:text-[120px]">
              LEVEL <br />
              <span className="text-amber-400">UP</span> <span className="tok-text-stroke">XR.</span>
            </h1>
            <p className="mt-10 text-lg sm:text-xl text-zinc-200 max-w-2xl leading-relaxed font-display">
              Where culture, technology, performance, wellbeing, gaming and
              immersive storytelling collide. A talent development and future
              opportunities pipeline for young creatives from the global
              majority.
            </p>
            <div className="mt-12 flex flex-wrap gap-4">
              <button
                onClick={() => scrollToForm("apply")}
                data-testid="hero-apply-cta"
                className="inline-flex items-center gap-3 bg-amber-400 hover:bg-amber-300 text-black px-8 py-5 font-bold uppercase tracking-[0.25em] text-xs transition-colors"
              >
                <Sparkles className="h-4 w-4" />
                Apply Now
              </button>
              <button
                onClick={() => scrollToForm("apply")}
                data-testid="hero-register-cta"
                className="inline-flex items-center gap-3 border border-amber-400/40 hover:border-amber-400 text-amber-400 px-8 py-5 font-bold uppercase tracking-[0.25em] text-xs transition-colors"
              >
                Register Interest
              </button>
              <button
                onClick={() => scrollToForm("partner")}
                data-testid="hero-partner-cta"
                className="inline-flex items-center gap-3 border border-white/20 hover:border-amber-400 hover:text-amber-400 text-white px-8 py-5 font-bold uppercase tracking-[0.25em] text-xs transition-colors"
              >
                Partner with us <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        </Container>
      </section>

      {/* WHO IT'S FOR */}
      <section
        data-testid="who-for-section"
        className="relative py-28 sm:py-36 border-y border-amber-500/10"
      >
        <Container>
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <FadeIn className="lg:col-span-5">
              <SectionLabel>Who Level Up is for</SectionLabel>
              <SectionHeading className="mt-6">
                Built for the <br />
                <span className="text-amber-400">overlooked</span>.
              </SectionHeading>
              <p className="mt-8 text-zinc-300 text-lg leading-relaxed max-w-md">
                Level Up XR is not just a creative course — it's a route in.
                Designed for young people the traditional system too often
                misses, and powerful enough to ignite the ones who are already
                burning.
              </p>
            </FadeIn>
            <FadeIn delay={0.15} className="lg:col-span-7">
              <ul className="grid sm:grid-cols-2 gap-3">
                {LEVEL_UP_FOR.map((line, i) => (
                  <li
                    key={line}
                    data-testid={`who-for-${i}`}
                    className="flex items-start gap-3 border border-white/5 bg-zinc-950 p-5 hover:border-amber-400/40 transition-colors"
                  >
                    <CheckCircle2 className="h-5 w-5 text-amber-400 mt-0.5 shrink-0" />
                    <span className="font-display font-semibold text-base leading-tight">
                      {line}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="mt-8 text-sm text-zinc-500 leading-relaxed">
                Fully funded places are available — nobody is locked out by
                cost. No prior experience required.
              </p>
            </FadeIn>
          </div>
        </Container>
      </section>

      {/* THE 12 TRACKS */}
      <section
        data-testid="tracks-section"
        className="py-28 sm:py-36 overflow-hidden"
      >
        <Container>
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
            <FadeIn>
              <SectionLabel>What you'll build</SectionLabel>
              <SectionHeading className="mt-6">
                Twelve disciplines. <br />
                <span className="text-amber-400">One creative future.</span>
              </SectionHeading>
            </FadeIn>
            <p className="text-zinc-400 max-w-md">
              Real projects: immersive experiences, performances, digital
              installations, interactive worlds, future-facing cultural work.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {LEVEL_UP_TRACKS.map((t, i) => (
              <FadeIn key={t.key} delay={(i % 6) * 0.04}>
                <div
                  data-testid={`track-${t.key}`}
                  className="border border-white/5 bg-zinc-950 p-7 h-full hover:border-amber-400/40 transition-colors group"
                >
                  <p className="text-[10px] uppercase tracking-[0.3em] text-amber-400 font-bold">
                    {String(i + 1).padStart(2, "0")}
                  </p>
                  <h3 className="mt-4 font-display font-bold uppercase text-xl tracking-tight">
                    {t.title}
                  </h3>
                  <p className="mt-3 text-sm text-zinc-300 leading-relaxed">
                    {t.desc}
                  </p>
                </div>
              </FadeIn>
            ))}
          </div>
        </Container>
      </section>

      {/* DISCIPLINE PHOTO STRIP — 6 unique cinematic images */}
      <section className="py-12 bg-black overflow-hidden">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-[3px]">
          {[
            { src: LEVEL_UP_IMG.projection, label: "Projection mapping" },
            { src: LEVEL_UP_IMG.ai, label: "AI creation" },
            { src: LEVEL_UP_IMG.unreal, label: "Unreal Engine / XR" },
            { src: LEVEL_UP_IMG.performance, label: "Performance" },
            { src: LEVEL_UP_IMG.skills, label: "Studio practice" },
            { src: LEVEL_UP_IMG.cohort, label: "The cohort" },
          ].map((tile, i) => (
            <div
              key={i}
              data-testid={`discipline-tile-${i}`}
              className="relative aspect-square overflow-hidden group"
            >
              <div
                className="absolute inset-0 bg-cover bg-center transition-transform duration-[1500ms] group-hover:scale-110"
                style={{ backgroundImage: `url(${tile.src})` }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
              <p className="absolute bottom-3 left-3 right-3 text-[10px] uppercase tracking-[0.3em] text-amber-400 font-bold opacity-0 group-hover:opacity-100 transition-opacity">
                {tile.label}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* OUTCOMES */}
      <section
        data-testid="outcomes-section"
        className="relative py-28 sm:py-36 overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover bg-center opacity-15"
          style={{ backgroundImage: `url(${LEVEL_UP_IMG.outcomes})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/85 to-black" />
        <Container className="relative">
          <FadeIn>
            <div className="inline-flex items-center gap-2 px-4 py-2 border border-amber-400/40 text-amber-400 text-[10px] uppercase tracking-[0.35em] font-bold mb-8">
              <Heart className="h-3.5 w-3.5" />
              What changes
            </div>
            <SectionHeading className="max-w-3xl">
              Real outcomes <br />
              for <span className="text-amber-400">real lives.</span>
            </SectionHeading>
          </FadeIn>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {LEVEL_UP_OUTCOMES.map((o, i) => (
              <FadeIn key={o.h} delay={(i % 5) * 0.05}>
                <div
                  data-testid={`outcome-${slug(o.h)}`}
                  className="border border-white/5 bg-zinc-950/85 backdrop-blur-md p-6 h-full"
                >
                  <p className="text-[10px] uppercase tracking-[0.3em] text-amber-400 font-bold">
                    {String(i + 1).padStart(2, "0")}
                  </p>
                  <h3 className="mt-4 font-display font-bold text-lg">{o.h}</h3>
                  <p className="mt-3 text-xs text-zinc-300 leading-relaxed">
                    {o.b}
                  </p>
                </div>
              </FadeIn>
            ))}
          </div>
        </Container>
      </section>

      {/* ACCREDITATION & PROGRESSION */}
      <section
        data-testid="progression-section"
        className="relative py-28 sm:py-36"
      >
        <Container>
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <FadeIn className="lg:col-span-5">
              <div className="inline-flex items-center gap-2 px-4 py-2 border border-amber-400/40 text-amber-400 text-[10px] uppercase tracking-[0.35em] font-bold mb-8">
                <Award className="h-3.5 w-3.5" />
                Accreditation & Progression
              </div>
              <SectionHeading>
                Not a workshop. <br />
                <span className="text-amber-400">A pipeline.</span>
              </SectionHeading>
              <p className="mt-8 text-zinc-300 text-lg leading-relaxed max-w-md">
                Level Up XR is a genuine talent development route. Every
                participant leaves with a certificate, a portfolio, mentors and
                real next steps mapped to their ambition.
              </p>
            </FadeIn>
            <div className="lg:col-span-7 space-y-3">
              {LEVEL_UP_PROGRESSION.map((p, i) => (
                <FadeIn key={p.h} delay={i * 0.05}>
                  <div
                    data-testid={`progression-${slug(p.h)}`}
                    className="border border-white/5 bg-zinc-950 p-7 hover:border-amber-400/40 transition-colors"
                  >
                    <div className="flex items-start gap-5">
                      <span className="text-amber-400 font-display font-black text-3xl leading-none w-12 shrink-0">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <div>
                        <h3 className="font-display font-bold text-xl">{p.h}</h3>
                        <p className="mt-2 text-zinc-300 leading-relaxed">{p.b}</p>
                      </div>
                    </div>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>

          {/* Institutions */}
          <div className="mt-20 border-t border-white/10 pt-16">
            <FadeIn>
              <SectionLabel>Future progression pathways</SectionLabel>
              <h3 className="mt-6 font-display font-black uppercase text-2xl sm:text-3xl tracking-tight leading-tight">
                Talking to the institutions <br />
                <span className="text-amber-400">that matter.</span>
              </h3>
            </FadeIn>
            <div className="mt-10 grid md:grid-cols-3 gap-4">
              {LEVEL_UP_INSTITUTIONS.map((inst, i) => (
                <FadeIn key={inst.name} delay={i * 0.06}>
                  <div
                    data-testid={`institution-${slug(inst.name)}`}
                    className="border border-amber-400/20 bg-zinc-950 p-7 h-full"
                  >
                    <GraduationCap className="h-7 w-7 text-amber-400" />
                    <h4 className="mt-5 font-display font-bold text-lg uppercase tracking-tight">
                      {inst.name}
                    </h4>
                    <p className="mt-3 text-sm text-zinc-300 leading-relaxed">
                      {inst.desc}
                    </p>
                  </div>
                </FadeIn>
              ))}
            </div>
            <p className="mt-8 text-xs text-zinc-500 leading-relaxed max-w-2xl">
              We work with universities, FE colleges, apprenticeship providers
              and creative industry partners to map every participant to the
              right next step.
            </p>
          </div>
        </Container>
      </section>

      {/* PARTNERSHIPS ECOSYSTEM */}
      <section
        data-testid="partnerships-section"
        className="relative py-28 sm:py-36 overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: `url(${LEVEL_UP_IMG.partnerships})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/85 to-black" />
        <Container className="relative">
          <FadeIn>
            <div className="inline-flex items-center gap-2 px-4 py-2 border border-amber-400/40 text-amber-400 text-[10px] uppercase tracking-[0.35em] font-bold mb-8">
              <Users className="h-3.5 w-3.5" />
              Partnership ecosystem
            </div>
            <SectionHeading className="max-w-3xl">
              Built together <br />
              with <span className="text-amber-400">aligned organisations.</span>
            </SectionHeading>
            <p className="mt-8 text-zinc-300 text-lg leading-relaxed max-w-2xl">
              We co-deliver Level Up XR with referrers, funders, hosts,
              commissioners and intervention partners. Here's who we work with
              — and the ways you can engage.
            </p>
          </FadeIn>

          <div className="mt-14 grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7">
              <SectionLabel>Partner types</SectionLabel>
              <ul className="mt-6 grid sm:grid-cols-2 gap-3">
                {LEVEL_UP_PARTNERS.map((p, i) => (
                  <li
                    key={p}
                    data-testid={`partner-type-${i}`}
                    className="border border-white/5 bg-zinc-950/80 backdrop-blur-md px-5 py-4 text-zinc-200 hover:border-amber-400/40 transition-colors text-sm"
                  >
                    {p}
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-5">
              <SectionLabel>Engagement modes</SectionLabel>
              <ul className="mt-6 space-y-3">
                {PARTNER_ENGAGEMENT.map((m, i) => (
                  <li
                    key={m}
                    data-testid={`engagement-mode-${i}`}
                    className="flex items-start gap-3"
                  >
                    <Handshake className="h-4 w-4 text-amber-400 mt-1 shrink-0" />
                    <span className="text-zinc-200 leading-relaxed">{m}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => scrollToForm("partner")}
                data-testid="partnerships-cta"
                className="mt-10 inline-flex items-center gap-3 bg-amber-400 hover:bg-amber-300 text-black px-7 py-4 font-bold uppercase tracking-[0.25em] text-xs transition-colors"
              >
                Start a partnership <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </Container>
      </section>

      {/* COHORT MOMENT */}
      <section className="py-24 sm:py-28">
        <Container>
          <FadeIn>
            <div className="relative aspect-[16/9] sm:aspect-[21/9] overflow-hidden border border-amber-400/20">
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${LEVEL_UP_IMG.cohort})` }}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/40 to-transparent" />
              <div className="absolute inset-0 flex items-center">
                <Container>
                  <div className="max-w-xl">
                    <p className="text-[11px] uppercase tracking-[0.35em] text-amber-400 font-bold">
                      The cohort
                    </p>
                    <h2 className="mt-6 font-display font-black uppercase leading-[0.95] tracking-tighter text-3xl sm:text-4xl lg:text-5xl">
                      You won't <br />
                      <span className="text-amber-400">do it alone.</span>
                    </h2>
                    <p className="mt-6 text-zinc-200 leading-relaxed">
                      Twenty creators per cohort. Real friendships, real
                      collaborations, a network you'll carry for years.
                    </p>
                  </div>
                </Container>
              </div>
            </div>
          </FadeIn>
        </Container>
      </section>

      {/* SCHEDULED PROGRAMME — Acuity discovery slot */}
      <section className="py-20 sm:py-24 bg-zinc-950 border-t border-amber-500/10">
        <Container>
          <FadeIn className="mb-10 max-w-3xl">
            <SectionLabel>Before you apply</SectionLabel>
            <SectionHeading className="mt-5">
              Talk to us <br />
              <span className="text-amber-400">before you commit.</span>
            </SectionHeading>
            <p className="mt-5 text-zinc-300 leading-relaxed">
              Not sure if LEVEL UP is right for you, your portfolio, or your
              timeline? Book a 20-minute scheduled call with our cohort lead —
              free, no obligation, no application required.
            </p>
          </FadeIn>
          <div className="grid md:grid-cols-2 gap-5 max-w-3xl">
            <AcuityScheduleBlock
              eyebrow="16 – 25"
              title="Discovery call with the cohort lead"
              copy="20 minutes. Talk through the pathway, portfolio expectations, funding, accessibility."
              slug="level-up-interviews"
              audience="16 – 25"
              duration="20 mins"
              fallbackTo="#forms-section"
              fallbackLabel="Open application"
              testId="acuity-level-up-discovery"
            />
            <AcuityScheduleBlock
              eyebrow="Partners"
              title="Schools & council partnership call"
              copy="Briefing on how LEVEL UP plugs into your schools, alternative provision or council programming."
              slug="discovery-call"
              audience="Partners"
              duration="30 mins"
              fallbackTo="/corporate"
              fallbackLabel="Send a brief"
              testId="acuity-level-up-partner"
            />
          </div>
        </Container>
      </section>

      {/* LEVEL UP — partner ecosystem (councils, youth, funders) */}
      <section className="py-20 sm:py-24 bg-zinc-950 border-t border-amber-500/10">
        <PartnersStrip
          eyebrow="LEVEL UP ecosystem"
          title="Built with real partners, not in isolation"
          copy="Our 16–25 pathway runs alongside Medway Youth, Hypha Studios, Medway and Kent councils, and Arts Council England — meaning every cohort sits inside a wider creative-industry network from day one."
          programmeKey="level-up"
          variant="card"
          testId="level-up-partners"
        />
      </section>

      {/* DUAL FORMS — Apply / Partner */}
      <section
        ref={formRef}
        data-testid="forms-section"
        className="py-28 sm:py-36 border-t border-amber-500/10 bg-black"
      >
        <Container>
          {/* Tab toggle */}
          <div className="flex flex-wrap gap-2 mb-10">
            <button
              onClick={() => setTab("apply")}
              data-testid="tab-apply"
              className={`px-6 py-4 text-[11px] uppercase tracking-[0.25em] font-bold border transition-colors ${
                tab === "apply"
                  ? "bg-amber-400 text-black border-amber-400"
                  : "border-white/10 text-zinc-300 hover:border-amber-400 hover:text-amber-400"
              }`}
            >
              Apply / Register · 16–25
            </button>
            <button
              onClick={() => setTab("partner")}
              data-testid="tab-partner"
              className={`px-6 py-4 text-[11px] uppercase tracking-[0.25em] font-bold border transition-colors ${
                tab === "partner"
                  ? "bg-amber-400 text-black border-amber-400"
                  : "border-white/10 text-zinc-300 hover:border-amber-400 hover:text-amber-400"
              }`}
            >
              Partner with us
            </button>
          </div>

          {tab === "apply" && (
            <div className="grid lg:grid-cols-12 gap-12">
              <FadeIn className="lg:col-span-5">
                <SectionLabel>Application</SectionLabel>
                <SectionHeading className="mt-6">
                  Join the <br />
                  <span className="text-amber-400">programme.</span>
                </SectionHeading>
                <p className="mt-6 text-zinc-300 leading-relaxed">
                  One form for Apply / Register / Join. We read every
                  application and reply within 5 working days.
                </p>
                <ul className="mt-8 space-y-3 text-sm text-zinc-400">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
                    Ages 16–25 · UK-based
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
                    Fully funded places available
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
                    No prior experience required
                  </li>
                </ul>
              </FadeIn>

              <FadeIn delay={0.1} className="lg:col-span-7">
                <form
                  onSubmit={submitApp}
                  data-testid="application-form"
                  className="border border-amber-500/15 bg-zinc-950 p-8 sm:p-10 grid sm:grid-cols-2 gap-5"
                >
                  <Field label="First name *" name="first_name" value={appForm.first_name} onChange={onAppChange} testId="app-first-name" required />
                  <Field label="Last name *" name="last_name" value={appForm.last_name} onChange={onAppChange} testId="app-last-name" required />
                  <Field label="Email *" type="email" name="email" value={appForm.email} onChange={onAppChange} testId="app-email" required />
                  <Field label="Phone" name="phone" value={appForm.phone} onChange={onAppChange} testId="app-phone" />
                  <Field label="Age" name="age" placeholder="16–25" value={appForm.age} onChange={onAppChange} testId="app-age" />
                  <Field label="City" name="city" value={appForm.city} onChange={onAppChange} testId="app-city" />

                  <div className="sm:col-span-2">
                    <Label>What pulls you in? (pick any)</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {LEVEL_UP_INTERESTS.map((i) => {
                        const selected = appForm.interests.includes(i);
                        return (
                          <button
                            key={i}
                            type="button"
                            data-testid={`interest-${slug(i)}`}
                            onClick={() => toggleAppInterest(i)}
                            className={`px-4 py-2 text-[11px] uppercase tracking-[0.2em] font-bold border transition-colors ${
                              selected
                                ? "bg-amber-400 text-black border-amber-400"
                                : "border-white/10 text-zinc-300 hover:border-amber-400 hover:text-amber-400"
                            }`}
                          >
                            {i}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <Field label="Portfolio / link (optional)" name="portfolio_url" placeholder="instagram, behance, github, anything" value={appForm.portfolio_url} onChange={onAppChange} testId="app-portfolio" />
                  <Field label="How did you hear about us?" name="referral_source" value={appForm.referral_source} onChange={onAppChange} testId="app-referral" />

                  <div className="sm:col-span-2">
                    <Label>Why Level Up XR for you? *</Label>
                    <textarea
                      data-testid="app-why-join"
                      required
                      name="why_join"
                      rows={5}
                      value={appForm.why_join}
                      onChange={onAppChange}
                      placeholder="Tell us about your creative story so far and what'll change if you get in."
                      className="w-full bg-black border border-white/10 px-4 py-3.5 text-white placeholder:text-zinc-600 focus:outline-none focus:border-amber-400 transition-colors resize-none"
                    />
                  </div>

                  <div className="sm:col-span-2 flex flex-wrap items-center gap-4 mt-2">
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="application-submit"
                      className="inline-flex items-center gap-3 bg-amber-400 hover:bg-amber-300 disabled:opacity-60 text-black px-8 py-5 font-bold uppercase tracking-[0.25em] text-xs transition-colors"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4" /> Send application
                        </>
                      )}
                    </button>
                    {appDone && (
                      <p data-testid="application-success" className="text-amber-400 text-sm flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4" /> Received. We'll read it and come back.
                      </p>
                    )}
                  </div>
                </form>
              </FadeIn>
            </div>
          )}

          {tab === "partner" && (
            <div className="grid lg:grid-cols-12 gap-12">
              <FadeIn className="lg:col-span-5">
                <SectionLabel>Partnership enquiry</SectionLabel>
                <SectionHeading className="mt-6">
                  Build with <br />
                  <span className="text-amber-400">us.</span>
                </SectionHeading>
                <p className="mt-6 text-zinc-300 leading-relaxed">
                  For youth organisations, local authorities, NHS partners,
                  schools, colleges, PRUs, alternative providers, housing
                  associations, universities and creative industry partners.
                </p>
                <p className="mt-6 text-sm text-zinc-500 leading-relaxed">
                  Pick the ways you'd like to engage and we'll come back within
                  2 working days.
                </p>
              </FadeIn>
              <FadeIn delay={0.1} className="lg:col-span-7">
                <form
                  onSubmit={submitPartner}
                  data-testid="partnership-form"
                  className="border border-amber-500/15 bg-zinc-950 p-8 sm:p-10 grid sm:grid-cols-2 gap-5"
                >
                  <Field label="Organisation *" name="organisation" value={partnerForm.organisation} onChange={onPartnerChange} testId="partner-organisation" required />
                  <div>
                    <Label>Organisation type *</Label>
                    <select
                      data-testid="partner-organisation-type"
                      required
                      name="organisation_type"
                      value={partnerForm.organisation_type}
                      onChange={onPartnerChange}
                      className="w-full bg-black border border-white/10 px-4 py-3.5 text-white focus:outline-none focus:border-amber-400 transition-colors"
                    >
                      {LEVEL_UP_PARTNERS.map((p) => (
                        <option key={p}>{p}</option>
                      ))}
                      <option>Other</option>
                    </select>
                  </div>
                  <Field label="Contact name *" name="contact_name" value={partnerForm.contact_name} onChange={onPartnerChange} testId="partner-contact-name" required />
                  <Field label="Your role" name="role" value={partnerForm.role} onChange={onPartnerChange} testId="partner-role" />
                  <Field label="Email *" type="email" name="email" value={partnerForm.email} onChange={onPartnerChange} testId="partner-email" required />
                  <Field label="Phone" name="phone" value={partnerForm.phone} onChange={onPartnerChange} testId="partner-phone" />
                  <Field label="Location" name="location" value={partnerForm.location} onChange={onPartnerChange} testId="partner-location" />
                  <Field label="Approx participants" name="participant_count" placeholder="e.g. 12 young people" value={partnerForm.participant_count} onChange={onPartnerChange} testId="partner-participant-count" />

                  <div className="sm:col-span-2">
                    <Label>How would you like to engage?</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {PARTNER_ENGAGEMENT.map((m) => {
                        const selected = partnerForm.engagement_modes.includes(m);
                        return (
                          <button
                            key={m}
                            type="button"
                            data-testid={`engagement-${slug(m)}`}
                            onClick={() => togglePartnerMode(m)}
                            className={`px-4 py-2 text-[11px] uppercase tracking-[0.2em] font-bold border transition-colors ${
                              selected
                                ? "bg-amber-400 text-black border-amber-400"
                                : "border-white/10 text-zinc-300 hover:border-amber-400 hover:text-amber-400"
                            }`}
                          >
                            {m}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <Label>What are you imagining? *</Label>
                    <textarea
                      data-testid="partner-message"
                      required
                      name="message"
                      rows={5}
                      value={partnerForm.message}
                      onChange={onPartnerChange}
                      placeholder="Outline your goals, your cohort, your timeline, and any context we should know."
                      className="w-full bg-black border border-white/10 px-4 py-3.5 text-white placeholder:text-zinc-600 focus:outline-none focus:border-amber-400 transition-colors resize-none"
                    />
                  </div>

                  <div className="sm:col-span-2 flex flex-wrap items-center gap-4 mt-2">
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="partnership-submit"
                      className="inline-flex items-center gap-3 bg-amber-400 hover:bg-amber-300 disabled:opacity-60 text-black px-8 py-5 font-bold uppercase tracking-[0.25em] text-xs transition-colors"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                        </>
                      ) : (
                        <>
                          <Briefcase className="h-4 w-4" /> Send enquiry
                        </>
                      )}
                    </button>
                    {partnerDone && (
                      <p data-testid="partnership-success" className="text-amber-400 text-sm flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4" /> Received. We'll be in touch shortly.
                      </p>
                    )}
                  </div>
                </form>
              </FadeIn>
            </div>
          )}
        </Container>
      </section>

      {/* WhatsApp opt-in — applications open, casting calls, intake reminders */}
      <StayConnected
        variant="whatsapp"
        pageSource="level-up-xr"
        testid="level-up-stay"
        whatsappHeadline="GET NOTIFIED WHEN APPLICATIONS OPEN"
        whatsappTagline="LEVEL UP cohorts fill fast. The WhatsApp Channel is where intake dates and casting calls drop first."
        whatsappCta="Join the Channel"
        bullets={[
          "Application windows & deadlines",
          "Open days and taster sessions",
          "Casting calls and paid creative work",
          "Funding and bursary announcements",
          "Pathway updates into University of Kent & Rose Bruford",
        ]}
      />
    </div>
  );
}

function Label({ children }) {
  return (
    <label className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 mb-2 block font-bold">
      {children}
    </label>
  );
}

function Field({ label, testId, ...rest }) {
  return (
    <div>
      <Label>{label}</Label>
      <input
        data-testid={testId}
        {...rest}
        className="w-full bg-black border border-white/10 px-4 py-3.5 text-white placeholder:text-zinc-600 focus:outline-none focus:border-amber-400 transition-colors"
      />
    </div>
  );
}
